import { useEffect, useMemo } from "react";
import { BatteryWarning, CalendarClock, Sun } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FieldMetric } from "./FieldLayout";
import { rtctApi } from "services/rtctApi";
import {
  bannerAlerts,
  daysText,
  readFleet,
  readPower,
  runwayText,
  dayBasisText,
  worstSeverity,
} from "utils/batteryRunway";
import { useStableState } from "utils/stableState";

// Battery runway (GET devices/<mac>/power/): how many days the node has left
// at its schedule, its daily solar harvest against its energy use, and the
// battery warnings from the first batch's post-mortem, worked out by the
// server (padihat/rtct/power.py). The answer changes slowly (days, not
// seconds), so it is asked for every 5 minutes, and kept as read by
// readPower so an identical answer redraws nothing.
const POLL_MS = 5 * 60 * 1000;

function usePolled(loader, key) {
  const [state, setState] = useStableState({ key: "", data: null, error: "" });
  useEffect(() => {
    // An older server (or a test that mocks the API without it) has no
    // battery runway: draw nothing rather than fail.
    if (!key || typeof loader !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(loader(signal))
        .then((data) => {
          if (!signal.aborted) setState({ key, data, error: "" });
        })
        .catch((e) => {
          // A failed poll keeps what is on screen (CLAUDE.md, "Do not redraw").
          if (!signal.aborted)
            setState((old) => ({ ...old, key, error: e?.message || "Battery runway unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [key, loader, setState]);
  return state.key === key ? state : { key, data: null, error: "" };
}

// One node's answer, read; null until (and unless) the server has one.
export function usePower(mac) {
  const loader = useMemo(
    () =>
      typeof rtctApi.power === "function" && mac
        ? (signal) => rtctApi.power(mac, signal).then(readPower)
        : null,
    [mac],
  );
  return usePolled(loader, mac).data;
}

// Every node with an active battery warning (GET power/), for the Devices page.
export function useFleetPower() {
  const loader = useMemo(
    () =>
      typeof rtctApi.fleetPower === "function"
        ? (signal) => rtctApi.fleetPower(signal).then(readFleet)
        : null,
    [],
  );
  return usePolled(loader, "fleet").data || [];
}

// The banner on the node pages: only while the node has a battery warning
// (none on external power), the most urgent first, with the days left.
export function BatteryBanner({ power }) {
  const alerts = bannerAlerts(power);
  if (!alerts.length) return null;
  const severity = worstSeverity(alerts);
  const runway = runwayText(power);
  const [first, ...rest] = alerts;
  return (
    <div
      className={`field-battery-banner is-${severity}`}
      role="status"
      aria-label={severity === "critical" ? "Battery critical" : "Battery warning"}
    >
      <BatteryWarning size={18} aria-hidden="true" />
      <div>
        <b>Battery:</b> {first.reason}.
        {runway && <span className="field-battery-runway"> {runway}.</span>}
        {rest.length > 0 && (
          <span className="field-battery-more">
            {" "}
            Also: {rest.map((a) => a.title.toLowerCase()).join(", ")}.
          </span>
        )}
      </div>
    </div>
  );
}

const mah = (v) => (v === null || v === undefined ? "–" : `${Math.round(v).toLocaleString()} mAh`);

function DayTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="field-battery-tip">
      <b>{row.date}</b>
      {!row.complete && <span> (partial day)</span>}
      <div>Harvest {mah(row.harvest)}</div>
      <div>Use {mah(row.use)}</div>
      {dayBasisText(row) && <div className="field-battery-tip-note">{dayBasisText(row)}</div>}
    </div>
  );
}

// Battery & solar on Node control: the days left at this schedule, the
// battery, harvest against use for the last 14 node-local days and the
// warnings in plain words. Harvest is what reaches the battery (the charger's
// input current x its voltage x 0.85 / the battery's); a day counts once its
// readings cover most of it (faded bars are partial days).
export function BatteryCard({ power }) {
  if (!power) return null;
  const f = power.forecast;
  const b = power.battery;
  const bal = power.balance;
  const runway = runwayText(power);
  const severity = worstSeverity(bannerAlerts(power));
  const state = power.externalPower
    ? ["External power", "is-off"]
    : severity === "critical"
      ? ["Critical", "is-critical"]
      : severity
        ? ["Warning", "is-warning"]
        : ["OK", "is-running"];
  return (
    <section className="field-panel field-battery-card" aria-label="Battery & solar">
      <div className="field-panel-head">
        <div>
          <h2>Battery &amp; solar</h2>
          <p>From the node's readings</p>
        </div>
        <span className={`field-state ${state[1]}`}>{state[0]}</span>
      </div>
      <div className="field-panel-body">
        <div className="field-metrics field-battery-metrics">
          <FieldMetric
            icon={CalendarClock}
            label="Days left"
            value={f.daysLeft !== null ? daysText(f.daysLeft) : runway || "Not yet known"}
            detail={
              f.daysLeft !== null && f.range && f.range[0] !== null
                ? `${daysText(f.range[0])} to ${daysText(f.range[1])}, ${f.confidence} confidence${f.basis ? `, from the ${f.basis}` : ""}`
                : null
            }
          />
          <FieldMetric
            icon={BatteryWarning}
            label="Battery"
            value={b.v !== null ? `${b.v.toFixed(3)} V` : "–"}
            detail={b.soc ? `About ${b.soc[0]}–${b.soc[2]} % charged` : null}
          />
          <FieldMetric
            icon={Sun}
            label="Harvest / use a day"
            value={bal && bal.harvest !== null ? `${Math.round(bal.harvest)} / ${Math.round(bal.use)}` : "–"}
            detail={bal ? `mAh, the last ${bal.days} complete day${bal.days === 1 ? "" : "s"}` : "No complete day yet"}
          />
        </div>
        {power.alerts.length > 0 && !power.externalPower && (
          <ul className="field-battery-alerts" aria-label="Battery warnings">
            {power.alerts.map((a) => (
              <li key={a.rule} className={`is-${a.severity}`}>
                <b>{a.title}</b>: {a.reason}.{a.help && <small> Rule: {a.help}.</small>}
              </li>
            ))}
          </ul>
        )}
        {power.days.length > 0 && (
          <div className="field-battery-chart" aria-label="Harvest and use per day, last 14 days">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={power.days} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#e8eff1" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis width={38} tick={{ fontSize: 10 }} unit="" />
                <Tooltip content={<DayTip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {[
                  ["harvest", "Harvest (mAh)", "#2f7d32"],
                  ["use", "Use (mAh)", "#b7791f"],
                ].map(([key, name, fill]) => (
                  <Bar key={key} dataKey={key} name={name} fill={fill} isAnimationActive={false}>
                    {power.days.map((d) => (
                      <Cell key={d.date} fillOpacity={d.complete ? 1 : 0.45} />
                    ))}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="field-caption">
          {f.daysLeft === null && f.text ? `${f.text} ` : ""}Faded bars are partial days. Between 3.27 and 3.33 V
          (about 30–95 % charged) the voltage barely changes, so the estimate is rough there.
        </p>
      </div>
    </section>
  );
}

// The Devices page: one line per node with a battery warning, nothing
// otherwise.
export function FleetBatteryBanner({ nodes }) {
  if (!nodes?.length) return null;
  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
      role="status"
      aria-label="Battery warnings"
    >
      <p className="text-xs font-bold uppercase tracking-wide">Battery warnings</p>
      <ul className="mt-1 space-y-0.5 text-sm">
        {nodes.map((n) => (
          <li key={n.mac}>
            <b>{n.name}</b>: {n.alerts[0].reason}
            {n.daysLeft !== null ? ` · about ${daysText(n.daysLeft)} left` : ""}
            {n.alerts.length > 1 ? ` (+${n.alerts.length - 1} more)` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}
